import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { CheckoutContactForm, CheckoutValidator, ValidationErrorKeys, ValidationErrors } from '@checkout-v2/const';
import { CheckoutActions, CheckoutSelectors } from '@checkout-v2/data-access';
import { Store } from '@ngrx/store';
import { ExtendedCustomFormValidators } from '@shared/helpers';
import { debounceTime, map, Observable, startWith } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CheckoutContactFormService extends CheckoutValidator {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  private readonly form = this.formBuilder.group({
    phone: ['', [Validators.required, Validators.minLength(10)]],
    email: ['', [ExtendedCustomFormValidators.emailValidator, Validators.maxLength(255)]],
    comment: ['', [Validators.maxLength(255)]],
  });

  constructor() {
    super();

    // синхронизация состояния формы со стором
    // обновлять стор при изменении формы
    this.form.valueChanges.pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.store.dispatch(new CheckoutActions.SetCheckoutContacts(value));
    });

    // синхронизация состояния формы со стором
    // обновлять форму при изменении стора
    this.store
      .select(CheckoutSelectors.getContacts)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value) {
          this.form.patchValue(value, { emitEvent: false });
        } else {
          this.form.reset(value, { emitEvent: false });
        }
      });
  }

  getInstance(): FormGroup {
    return this.form;
  }

  reset(): void {
    this.form.reset();
  }

  getValue(): CheckoutContactForm {
    return this.form.value;
  }

  setValue(value: CheckoutContactForm): void {
    this.form.patchValue(value);
  }

  getValidationErrors(): Observable<ValidationErrors> {
    return this.form.statusChanges.pipe(
      map((status) => status === 'VALID'),
      startWith(this.form.valid),
      map((isValid) => {
        const errors: ValidationErrors = {};

        if (!isValid) {
          errors[ValidationErrorKeys.CONTACTS_REQUIRED] = 'заполните контакты';
        }

        return errors;
      }),
    );
  }
}
